import type { FieldListItemTyped } from '#blokkli-build/generated-types'
import { toValidHtmlId } from '~/helpers'
import type { TeaserColumns } from '~/helpers/paragraphs'

type ParagraphContext = {
  teaserColumns: TeaserColumns
  teaserDivided: boolean
  teaserNumbered: boolean
}

export function useParagraphContext<T extends keyof ParagraphContext>(
  key: T,
  defaultValue: ParagraphContext[T],
): ComputedRef<ParagraphContext[T]> {
  const injected = inject<ComputedRef<ParagraphContext[T]> | null>(key, null)

  return computed(() => {
    return injected?.value || defaultValue
  })
}

export function provideParagraphContext<T extends keyof ParagraphContext>(
  key: T,
  value: ComputedRef<ParagraphContext[T]>,
) {
  /* eslint-disable  @typescript-eslint/no-explicit-any */
  provide(key, value as any)
}

/**
 * Determine the value of the ID attribute for a paragraph.
 *
 * The title paragraph can be configured to only being displayed in the anchor
 * nav and not actually rendered in the DOM. In this case, the paragraph next
 * in the list will become the target for the anchor link.
 */
export function useParagraphAnchorId(
  index: ComputedRef<number>,
  siblings: ComputedRef<FieldListItemTyped[]>,
): ComputedRef<string | undefined> {
  return computed(() => {
    const prevParagraph = siblings.value[index.value - 1]
    if (!prevParagraph) {
      return undefined
    }

    // The previous paragraph is a "anchor only" title paragraph.
    if (
      prevParagraph.bundle === 'title' &&
      prevParagraph.options.anchorOnly === '1'
    ) {
      const label =
        prevParagraph.props?.anchorTitle || prevParagraph.props?.title
      if (label) {
        return toValidHtmlId(label)
      }
    }

    return undefined
  })
}
